import type Dexie from 'dexie';
import type { Collection, IndexableType } from 'dexie';
import idbCollection from './idb.collection';
import type { IDBKeyType, ITable } from './interface';

function IndexedTable<DataType extends Record<IDBKeyType, unknown>, IDType extends keyof DataType>(
    dexie: Dexie,
    tableName: string,
): ITable<DataType, IDType> {
    const dexieTable = dexie.table(tableName);

    const table: ITable<DataType, IDType> = {
        get: async (key: IDType) => {
            /* See PWA-216 */
            if (tableName === 'ISiteObjectOnFloorPlanDto') {
                throw new Error(`db.get is not supported for composite primary keys`);
            }

            const result = (await dexieTable.get(key as IndexableType)) as
                | DataType
                | undefined
                | null;

            if (result === undefined || result === null) {
                throw new Error(`No entry in table:${tableName} for key: ${String(key)}`);
            }

            return result;
        },
        count: dexieTable.count.bind(dexieTable),
        toArray: dexieTable.toArray.bind(dexieTable),
        toCollection: () =>
            idbCollection(dexieTable.toCollection() as Collection<DataType, IDType>),
        add: async (item: DataType) => {
            const result = await dexieTable.add(item);
            return result as IDType;
        },
        update: async (key: IDType, changes: Partial<DataType>) => {
            const result = await dexieTable.update(key, changes);
            return result > 0;
        },
        addOrUpdate: async (key: IDType, changes: DataType) => {
            await dexieTable.put(changes, key as IndexableType | undefined);
            return true;
        },
        delete: dexieTable.delete.bind(dexieTable) as (key: IDType) => Promise<void>,
        clear: dexieTable.clear.bind(dexieTable),
        bulkGet: dexieTable.bulkGet.bind(dexieTable) as (key: IDType[]) => Promise<DataType[]>,

        bulkAdd: (items: DataType[]) =>
            dexieTable.bulkAdd(items, { allKeys: true }) as unknown as Promise<IDType[]>,
        bulkAddOrUpdate: (items: DataType[]) =>
            dexieTable.bulkPut(items, { allKeys: true }) as unknown as Promise<IDType[]>,
        bulkDelete: dexieTable.bulkDelete.bind(dexieTable) as (keys: IDType[]) => Promise<void>,
    };

    return table;
}

export default IndexedTable;
